import React, { useRef, useEffect } from 'react';
import { Calendar, CheckSquare, ExternalLink, ChevronDown } from 'lucide-react';
import Select from 'react-select';
import dayjs from 'dayjs';
import { message } from 'antd';
import { getSafeValue, formatDate, autoResizeTextarea } from '../utils/helpers';
import { dropdownOptions } from '../constants/dropdownOptions';

const TableCell = ({
  columnKey,
  value,
  rowIndex,
  colIndex,
  rowData,
  isEditing,
  editingValue,
  setEditingValue,
  handleEditingChange,
  finishEditing,
  handleEditingKeyDown,
  enterEditMode,
  currentSheetType,
  year,
  updateCells,
  updateData,
  activeCell,
  setEditingCell,
  setSelectedRequirement,
  setShowCompletionModal,
  isCompanyInClientList,
  replaceOnNextEdit,
  setSelectionStart,
  setSelectionEnd,
  setActiveCell
}) => {
  const inputRef = useRef(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      autoResizeTextarea(inputRef.current);

      const length = editingValue?.length || 0;

      if (replaceOnNextEdit.current) {
        inputRef.current.setSelectionRange(length, length);
      } else {
        inputRef.current.setSelectionRange(length, length);
      }
      replaceOnNextEdit.current = false;
    }
  }, [isEditing, editingValue]);

  const renderEditingCell = () => {
    if (columnKey === 'Due Date' || columnKey === 'Date' || columnKey === 'Date Confirmed') {
      const handleDateSave = (formattedDate) => {
        // Use the React Query mutation
        updateCells([{
          unid: rowData.unid,
          columnId: columnKey,
          value: formattedDate
        }]);

        setEditingCell(null);
        setEditingValue('');
      };

      return (
        <div className="relative">
          <input
            type="text"
            value={editingValue || ''}
            onChange={(e) => {
              let value = e.target.value.replace(/\D/g, '');
              if (value.length > 8) value = value.slice(0, 8);
              
              if (value.length >= 4) {
                value = value.slice(0, 4) + '-' + value.slice(4);
              }
              if (value.length >= 7) {
                value = value.slice(0, 7) + '-' + value.slice(7);
              }
              
              setEditingValue(value);
            }}
            onKeyDown={(e) => {
              const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter', '-'];
              if (!allowedKeys.includes(e.key) && !/^\d$/.test(e.key)) {
                e.preventDefault();
              }
              if (e.key === 'Enter' || e.key === 'Tab') {
                e.preventDefault();
                if (editingValue) {
                  const date = dayjs(editingValue);
                  if (date.isValid()) {
                    handleDateSave(date.format('YYYY-MM-DD')).then(() => {
                      const [row, col] = activeCell;
                      setSelectionStart([row + 1, col]);
                      setSelectionEnd([row + 1, col]);
                      setActiveCell([row + 1, col]);
                    });
                  } else {
                    const [row, col] = activeCell;
                    setEditingCell(null);
                    setSelectionStart([row + 1, col]);
                    setSelectionEnd([row + 1, col]);
                    setActiveCell([row + 1, col]);
                  }
                } else {
                  const [row, col] = activeCell;
                  setEditingCell(null);
                  setSelectionStart([row + 1, col]);
                  setSelectionEnd([row + 1, col]);
                  setActiveCell([row + 1, col]);
                }
              }
            }}
            placeholder="YYYY-MM-DD"
            className="w-full h-full resize-none overflow-hidden text-inherit font-inherit 
              bg-transparent border-none outline-none p-0 m-0"
            autoFocus
          />
        </div>
      );
    } else if (dropdownOptions[columnKey]) {
      const currentOption = dropdownOptions[columnKey].find(option => option.value === editingValue) || dropdownOptions[columnKey][0];
      
      return (
        <Select
          value={currentOption}
          onChange={(selectedOption) => {
            setEditingValue(selectedOption.value);
            
            // Use the React Query mutation
            updateCells([{
              unid: rowData.unid,
              columnId: columnKey,
              value: selectedOption.value
            }]);

            setEditingCell(null);
            setEditingValue('');
          }}
          options={dropdownOptions[columnKey]}
          autoFocus
          openMenuOnFocus
          openMenuOnClick
          menuPortalTarget={document.body}
          menuPosition="fixed"
          styles={{
            control: (provided) => ({
              ...provided,
              minHeight: '100%',
              border: 'none',
              boxShadow: 'none',
              backgroundColor: 'transparent',
            }),
            menu: (provided) => ({
              ...provided,
              position: 'absolute',
              zIndex: 9999,
              backgroundColor: 'white',
              borderRadius: '0.5rem',
              boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
              overflow: 'visible',
            }),
            option: (provided, { data, isFocused, isSelected }) => ({
              ...provided,
              backgroundColor: isSelected ? data.color : isFocused ? `${data.color}20` : 'white',
              color: isSelected ? 'white' : data.color,
              cursor: 'pointer',
              padding: '0.75rem 1rem',
              fontSize: '0.875rem',
              fontWeight: '500',
              transition: 'all 150ms ease',
              '&:hover': {
                backgroundColor: `${data.color}30`,
              },
            }),
            singleValue: (provided, { data }) => ({
              ...provided,
              color: data.color,
              fontWeight: '500',
            }),
            input: (provided) => ({
              ...provided,
              color: 'inherit',
              margin: 0,
              padding: 0,
            }),
            indicatorsContainer: () => ({
              display: 'none',
            }),
            menuPortal: (provided) => ({
              ...provided,
              zIndex: 9999,
            }),
          }}
        />
      );
    } else {
      return (
        <textarea
          ref={inputRef}
          value={editingValue}
          onChange={handleEditingChange}
          onBlur={finishEditing}
          onKeyDown={handleEditingKeyDown}
          className="w-full h-full resize-none overflow-hidden text-inherit font-inherit bg-transparent border-none outline-none p-0 m-0"
          autoFocus
        />
      );
    }
  };

  const renderDisplayCell = () => {
    const safeValue = getSafeValue(value);

    // Company Name column with client status indicator for all sheets except Client List
    if (columnKey === 'Company Name' && currentSheetType !== 'Client List') {
      // Special handling for "All Clients" in Plan Touchpoints and Requirements Checklist
      const isAllClientsInSpecialSheets = safeValue === 'All Clients' && 
        (currentSheetType === 'Plan Touchpoints' || currentSheetType === 'Requirements Checklist');
      
      // Default to true (green) - only show red if we have a value and it's NOT in the list
      const isInClientList = !safeValue ? true : 
        (isAllClientsInSpecialSheets || isCompanyInClientList(safeValue));
      
      return (
        <div className="px-4 py-2 text-slate-700 font-medium flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full animate-pulse ${
            isInClientList 
              ? 'bg-gradient-to-br from-green-400 to-emerald-600' 
              : 'bg-gradient-to-br from-red-400 to-rose-600'
          }`}></div>
          {safeValue}
        </div>
      );
    }

    // Special handling for Complete column when Company Name is "All Clients"
    if (columnKey === 'Complete' && rowData['Company Name'] === 'All Clients') {
      return (
        <button
          onClick={() => {
            console.log('Opening completion modal for:', rowData);
            setSelectedRequirement(rowData);
            setShowCompletionModal(true);
          }}
          className="w-full px-4 py-2.5 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl 
            hover:from-indigo-600 hover:to-purple-700 transition-all duration-300 text-sm font-semibold 
            flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transform hover:scale-[1.02]
            relative overflow-hidden group"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 
            -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
          <CheckSquare size={18} className="relative z-10" />
          <span className="relative z-10">Mark Complete</span>
        </button>
      );
    }

    // Display formatted date when not editing
    if (columnKey === 'Due Date' || columnKey === 'Date' || columnKey === 'Date Confirmed') {
      const formattedDate = safeValue ? formatDate(safeValue) : '';

      return (
        <div
          className="flex items-center justify-between px-4 py-2 rounded-xl text-sm cursor-pointer
            bg-gradient-to-r from-slate-50 to-blue-50/30 hover:from-blue-50 hover:to-indigo-50/40
            group transition-all duration-300 relative border border-transparent hover:border-blue-200/50"
          onClick={() => enterEditMode(rowIndex, colIndex)}
          title="Click to select date"
        >
          <span className={`font-medium ${safeValue ? 'text-slate-700' : 'text-slate-400'}`}>
            {formattedDate || 'Select date'}
          </span>
          <Calendar 
            size={16} 
            className="text-slate-400 group-hover:text-blue-600 transition-all duration-300 group-hover:scale-110" 
          />
        </div>
      );
    }

    // For Risk Tolerance Report
    if (columnKey === 'Risk Tolerance Report') {
      const hasReport = !!safeValue;
      return (
        <button
          onClick={hasReport ? () => window.open(safeValue, '_blank') : null}
          disabled={!hasReport}
          title={!hasReport ? 'No Report Available' : 'Click to view report'}
          className={`flex items-center justify-center gap-2 px-4 py-2 ${
            hasReport 
              ? 'bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 text-white shadow-lg hover:shadow-xl' 
              : 'bg-gradient-to-r from-slate-100 to-slate-200 text-slate-400 cursor-not-allowed'
          } rounded-xl transition-all duration-300 w-full text-sm font-semibold transform hover:scale-[1.02] relative overflow-hidden group`}
        >
          <div className={`absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 
            -translate-x-full transition-transform duration-700 ${hasReport ? 'group-hover:translate-x-full' : ''}`}></div>
          <span className="relative z-10">{hasReport ? 'View Report' : 'No Report'}</span>
          {hasReport && <ExternalLink size={16} className="relative z-10" />}
        </button>
      );
    }

    // If it's a dropdown column, display the value with modern styling
    if (dropdownOptions[columnKey]) {
      const option = dropdownOptions[columnKey].find(opt => opt.value === safeValue);
      const displayValue = option ? option.label : safeValue;
      const color = option ? option.color : '#94a3b8';

      return (
        <div
          className="px-4 py-2 rounded-xl text-white text-sm font-semibold cursor-pointer 
            transition-all duration-300 hover:scale-[1.02] hover:shadow-lg flex items-center justify-between
            group relative overflow-hidden"
          style={{
            background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`,
            boxShadow: '0 4px 12px -2px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255,255,255,0.2)',
          }}
          onClick={() => {
            enterEditMode(rowIndex, colIndex);
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 
            -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
          <span className="relative z-10">{displayValue || 'Select...'}</span>
          <ChevronDown size={16} className="opacity-90 group-hover:opacity-100 relative z-10 
            transition-all duration-300 group-hover:translate-y-0.5" />
        </div>
      );
    }

    // Display the value directly
    return safeValue;
  };

  return isEditing ? renderEditingCell() : renderDisplayCell();
};

export default TableCell;